import axios, { AxiosRequestConfig, AxiosResponse, AxiosError, AxiosInstance, Method, CancelToken } from 'axios';

import SMHException, { SMHExceptionCodeType } from '../error';

export type RequestResponse<T> = Promise<AxiosResponse<T>>;

/** @description 请求名 */
export type APIName = string;

export interface CustomAttributes {
  apiName: APIName;
  spaceId?: string;
  shareToken?: string;
  syncId?: number;
  [key: string]: any;
}

declare module 'axios' {
  export interface AxiosRequestConfig {
    customAttributes?: CustomAttributes;
  }
}

export type HTTP = AxiosInstance;

/** @description 某一类请求构成的集合名 */
export type APISetName = string;

/** @description 请求中某一所需参数名 */
export type ParamKey =
  | string
  | {
      [key: string]: string;
    };

export interface ParamKeys {
  required?: ParamKey[];
  noRequired?: ParamKey[];
}

export interface ParamKeyObject {
  [key: string]: any;
}
/**
 * @description 请求体（一种特殊的对象）
 * 以对象数据类型描述的请求，
 * 将被转换成对应的请求函数被挂载到 DAO 上。
 * 其中，params 分为三个层级
 * 函数执行时传入的 params > autoParams > defaultParams
 */
export interface APIConfig {
  // HTTP请求方式
  method: Method;
  // 添加 URL 路径，占位符请使用 {variable}
  path: string;
  actionKey?: string;
  // GET 请求的
  queryKeys?: ParamKeys | 'custom';
  // POST请求放置在BODY中的参数
  bodyKeys?: ParamKeys | 'custom';
  // 需要SDK自动追加的参数，SDK中会替代对应的占位符${variable}
  // key-value 组合，value传空字符串
  autoParams?: ParamKeyObject;
  // 默认的初始化参数
  // 场景1：需要一些固定参数，例如pageSize=20，可以放在这里
  // 场景2：需要一些默认值，例如visible=false；默认为false；但是当autoParams传递了true，则使用true;
  defaultParams?: ParamKeyObject;
  // 是否开发模式，会导致代理到不同的接口
  isDebugger?: boolean;
}

export interface CustomParams<CustomQueryType, CustomBodyType> {
  query?: CustomQueryType;
  body?: CustomBodyType;
  isDebugger?: boolean;
}

interface APIFunction<CustomQueryType = null, CustomBodyType = null> {
  (incomingParams: ParamKeyObject, secondParams?: CancelToken | CustomParams<CustomQueryType, CustomBodyType>): void;
  (
    incomingParams: ParamKeyObject,
    cancelToken: CancelToken,
    customParams: CustomParams<CustomQueryType, CustomBodyType>,
  ): void;
}

/**
 * API 类型生成器
 * @param {any} ParamsType 入参类型
 * @param {any} DataType 出参类型
 */
export interface APIFunctionType<ParamsType, DataType, CustomQueryType = unknown, CustomBodyType = unknown> {
  (): RequestResponse<DataType>;
  (
    params: ParamsType,
    secondParams?: CancelToken | CustomParams<CustomQueryType, CustomBodyType>,
  ): RequestResponse<DataType>;
  (
    params: ParamsType,
    cancelToken: CancelToken,
    customParams: CustomParams<CustomQueryType, CustomBodyType>,
  ): RequestResponse<DataType>;
}

export interface APIConfigSet {
  [key: string]: APIConfig;
}

export type API = (params?: any, ...args: any[]) => RequestResponse<any>;

export interface APISet {
  [key: string]: API;
}

export type ParamsObject = {
  incomingParams: ParamKeyObject;
  autoParams: ParamKeyObject;
  defaultParams: ParamKeyObject;
};

export type APIPlugin = (apiSetName: APISetName, apiName: APIName, paramsObject: ParamsObject) => Promise<ParamsObject>;

export type ErrorHandler = (
  error: any,
  data: {
    apiSetName: APISetName;
    apiName: APIName;
    config: AxiosRequestConfig;
  },
) => Promise<AxiosError | AxiosResponse>;

export interface DAOConfig {
  http?: AxiosInstance;
  plugins?: APIPlugin[];
  errorHandler?: ErrorHandler;
  customAttributesKeys?: string[];
}

const DEFAULT_CUSTOM_PARAMS: CustomParams<null, null> = {
  query: null,
  body: null,
  isDebugger: false,
};

export default class DAO {
  /** 网络请求工具 */
  private http: HTTP = axios;

  /** 插件列表 */
  public plugins: APIPlugin[] = [];

  private errorHandler: ErrorHandler = async (error, data) => error;

  private customAttributesKeys: string[] = [];

  [key: string]: any;

  constructor(daoConfig?: DAOConfig) {
    daoConfig?.http && (this.http = daoConfig.http);
    daoConfig?.plugins?.length && (this.plugins = daoConfig.plugins);
    daoConfig?.errorHandler && (this.errorHandler = daoConfig.errorHandler);
    daoConfig?.customAttributesKeys && (this.customAttributesKeys = daoConfig.customAttributesKeys);
  }

  private async getVariablePool(
    apiSetName: APISetName,
    apiName: APIName,
    variablePool: {
      incomingParams: ParamKeyObject;
      autoParams: ParamKeyObject;
      defaultParams: ParamKeyObject;
    },
  ): Promise<ParamKeyObject> {
    for (const plugin of this.plugins) {
      // 不能使用 Promise.all
      // 【必须】按照插件顺序 小写转驼峰 -> 自动填充参数 -> 驼峰转小写 严格同步执行
      // eslint-disable-next-line no-await-in-loop
      Object.assign(variablePool, await plugin(apiSetName, apiName, { ...variablePool }));
    }
    return { ...variablePool.defaultParams, ...variablePool.autoParams, ...variablePool.incomingParams };
  }

  private mountAPI(apiSetName: APISetName, apiName: APIName, apiConfig: APIConfig, baseURL = ''): void {
    const {
      method,
      path,
      actionKey = '',
      queryKeys,
      bodyKeys,
      autoParams = {},
      defaultParams = {},
      isDebugger,
    } = apiConfig;
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const that = this;

    /**
     * 通过 APIConfig 描述文件生成的 APIFunctin 请求函数
     * @param incomingParams 传入的参数
     * @param customParams 自定义的 query
     * @param customData 自定义的请求体
     * @returns
     */
    function apiFunction<CustomQueryType = any, CustomBodyType = any>(
      incomingParams: ParamKeyObject,
      secondParams?: CancelToken | CustomParams<CustomQueryType, CustomBodyType>,
    ): Promise<unknown>;
    function apiFunction<CustomQueryType = any, CustomBodyType = any>(
      incomingParams: ParamKeyObject,
      cancelToken: CancelToken,
      customParams: CustomParams<CustomQueryType, CustomBodyType>,
    ): Promise<unknown>;
    async function apiFunction(
      _incomingParams = {},
      _secondParam: undefined | CancelToken | CustomParams<ParamKeyObject, any> = {},
      _thirdParam?: undefined | CustomParams<ParamKeyObject, any>,
    ): Promise<unknown> {
      const incomingParams = { ..._incomingParams };
      const secondParam = { ..._secondParam };
      const thirdParam = { ..._thirdParam };
      let customParams;
      let cancelToken;
      if (_secondParam && 'promise' in _secondParam) {
        cancelToken = _secondParam;
        customParams = { ...DEFAULT_CUSTOM_PARAMS, ...thirdParam };
      } else {
        cancelToken = undefined;
        customParams = { ...DEFAULT_CUSTOM_PARAMS, ...secondParam };
      }
      // 获取由传入的参数、自动从上下文获取的参数、默认参数组成的变量池
      const variablePool = await that.getVariablePool(apiSetName, apiName, {
        incomingParams,
        autoParams,
        defaultParams,
      });
      // 从变量池获取所需参数以填充 path ，并与 actionKey 组成 url
      const url = `${DAO.computURL(path, variablePool, baseURL)}${actionKey ? `?${actionKey}` : ''}`;

      // 从变量池获取所需参数以填充 params 与 data
      const params =
        queryKeys === 'custom'
          ? customParams.query
          : DAO.computParams(apiSetName, apiName, queryKeys, variablePool, 'query');
      const data =
        bodyKeys === 'custom'
          ? customParams.body
          : DAO.computParams(apiSetName, apiName, bodyKeys, variablePool, 'body');

      // 发 userApi 请求时单独处理全局组织 id 与操作对应的组织 id 不一致的情况
      // if (apiSetName === 'userAPI' && variablePool.spaceOrgId && variablePool?.spaceOrgId !== variablePool.organizationId) {
      //   if (params && ['get', 'delete'].includes(method.toLocaleLowerCase())) {
      //     params.space_org_id = variablePool.space_org_id;
      //   } else if (data && !Array.isArray(data) && ['put', 'post'].includes(method.toLocaleLowerCase())) {
      //     data.spaceOrgId = variablePool.spaceOrgId;
      //   }
      // }

      const customAttributes: CustomAttributes = { apiName };
      that.customAttributesKeys.forEach(key => {
        if (key in variablePool) {
          customAttributes[key] = variablePool[key];
        }
      });
      const config = {
        customAttributes,
        method,
        url,
        params,
        data,
        cancelToken,
      };
      if (isDebugger || customParams.isDebugger) debugger;
      try {
        const response = await that.http(config);
        return response;
      } catch (error: any) {
        if (error.message === 'canceled') {
          return Promise.reject(error);
        }
        const response = await that.errorHandler(error, { apiSetName, apiName, config });
        // 若 response 存在 data 则认为是重试后正常的请求，否则则抛出异常
        if ('data' in response) return response;
        return Promise.reject(error);
      }
    }
    // @ts-ignore
    this[apiSetName][apiName] = apiFunction;
  }

  /**
   * 挂载请求集
   * @param apiSetName
   * @param apiConfigSet
   */
  public mountAPISet<T extends APISet>(apiSetName: APISetName, apiConfigSet: APIConfigSet, baseURL?: string): T {
    // @ts-ignore
    this[apiSetName] = {};
    Object.keys(apiConfigSet).forEach(apiName => {
      this.mountAPI(apiSetName, apiName, apiConfigSet[apiName], baseURL);
    });
    return this[apiSetName];
  }

  private static computURL(path: string, variablePool: ParamKeyObject, baseURL = ''): string {
    let url = path;
    // step1: 提取出 path 中需要哪些 params ，这些 params 构成一个可空数组 pathParams
    const regex = /\{(.*?)\}/g;
    const pathParams = path.match(regex);
    // step2: 若 pathParams 为空，说明该 path 无参数，直接返回
    pathParams &&
      pathParams.forEach(Key => {
        const key = Key.replace(/^[{}]+|[}]+$/g, '');
        const value = variablePool[key];
        if (value === undefined || value === null) {
          throw new SMHException(
            SMHExceptionCodeType.NoRequiredParam,
            `[SMH Warning]: Missing parameter {${key}} to replace path, please check the configuration of the request.`,
          );
        }
        url = url.replace(new RegExp(`\\{${key}\\}`, 'g'), encodeURIComponent(value));
      });
    return baseURL + url;
  }

  private static computParams(
    apiSetName: APISetName,
    apiName: APIName,
    paramkeys: ParamKeys = {},
    variablePool: ParamKeyObject,
    paramType: 'query' | 'body' | string,
  ): ParamKeyObject {
    const params: { [key: string]: any } = {};
    let isRequired = true;
    paramkeys &&
      [paramkeys.required, paramkeys.noRequired].forEach(keyList => {
        keyList &&
          keyList.forEach(key => {
            const isObjectKey = typeof key === 'object';
            const realKey = (isObjectKey ? Object.keys(key)[0] : key) as string;
            const shadow = (isObjectKey ? Object.values(key)[0] : key) as string;
            const value = variablePool[shadow];
            if ((value === undefined || value === null) && isRequired) {
              throw new SMHException(
                SMHExceptionCodeType.NoRequiredParam,
                `[SMH Warning]: ${apiSetName} > ${apiName} The necessary parameter is missing, its key is { ${key} }.`,
              );
            }
            if (value !== undefined && value !== null) params[realKey] = value;
          });
        isRequired = !isRequired;
      });

    return params;
  }
}
